import { supabase } from '@/lib/supabase';

const functionsBase = () => {
  const url = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';
  return `${url}/functions/v1/bunny-storage`;
};

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Session expirée — reconnectez-vous');
  const anon = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)?.trim() ?? '';
  return {
    Authorization: `Bearer ${token}`,
    apikey: anon,
  };
}

export async function uploadContestDocument(opts: {
  registrationId: string;
  storagePath: string;
  file: File;
}): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(`${functionsBase()}?action=upload`, {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': opts.file.type || 'application/octet-stream',
      'x-storage-path': opts.storagePath,
      'x-registration-id': opts.registrationId,
    },
    body: opts.file,
  });
  const payload = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok || payload.error) throw new Error(payload.error || `Upload échoué (${res.status})`);
}

export async function deleteContestDocument(opts: {
  registrationId: string;
  storagePath: string;
}): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(`${functionsBase()}?action=delete`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      registration_id: opts.registrationId,
      storage_path: opts.storagePath,
    }),
  });
  const payload = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok || payload.error) throw new Error(payload.error || `Suppression échouée (${res.status})`);
}

export async function signContestDocumentUrl(opts: {
  registrationId: string;
  storagePath: string;
  ttlSec?: number;
}): Promise<string> {
  const headers = await authHeaders();
  const res = await fetch(`${functionsBase()}?action=sign`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      registration_id: opts.registrationId,
      storage_path: opts.storagePath,
      ttl_sec: opts.ttlSec ?? 3600,
    }),
  });
  const payload = (await res.json().catch(() => ({}))) as { signedUrl?: string; error?: string };
  if (!res.ok || payload.error || !payload.signedUrl) {
    throw new Error(payload.error || `URL signée inaccessible (${res.status})`);
  }
  return payload.signedUrl;
}

export async function uploadContestResourceDocument(opts: {
  storagePath: string;
  file: File;
}): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(`${functionsBase()}?action=upload&kind=resource`, {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': opts.file.type || 'application/octet-stream',
      'x-storage-path': opts.storagePath,
      'x-doc-kind': 'resource',
    },
    body: opts.file,
  });
  const payload = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok || payload.error) throw new Error(payload.error || `Upload échoué (${res.status})`);
}

export async function deleteContestResourceDocument(opts: { storagePath: string }): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(`${functionsBase()}?action=delete&kind=resource`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json', 'x-doc-kind': 'resource' },
    body: JSON.stringify({
      kind: 'resource',
      storage_path: opts.storagePath,
    }),
  });
  const payload = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok || payload.error) throw new Error(payload.error || `Suppression échouée (${res.status})`);
}

export async function signContestResourceDocumentUrl(opts: {
  storagePath: string;
  ttlSec?: number;
}): Promise<string> {
  const headers = await authHeaders();
  const res = await fetch(`${functionsBase()}?action=sign&kind=resource`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json', 'x-doc-kind': 'resource' },
    body: JSON.stringify({
      kind: 'resource',
      storage_path: opts.storagePath,
      ttl_sec: opts.ttlSec ?? 3600,
    }),
  });
  const payload = (await res.json().catch(() => ({}))) as { signedUrl?: string; error?: string };
  if (!res.ok || payload.error || !payload.signedUrl) {
    throw new Error(payload.error || `URL signée inaccessible (${res.status})`);
  }
  return payload.signedUrl;
}

export async function uploadMarketplaceImage(opts: {
  userId: string;
  file: File;
}): Promise<{ storage_path: string; url: string }> {
  const headers = await authHeaders();
  const safeName = opts.file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const storagePath = `marketplace/${opts.userId}/${Date.now()}-${safeName}`;
  const res = await fetch(`${functionsBase()}?action=upload&kind=marketplace`, {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': opts.file.type || 'application/octet-stream',
      'x-storage-path': storagePath,
      'x-doc-kind': 'marketplace',
    },
    body: opts.file,
  });
  const payload = (await res.json().catch(() => ({}))) as {
    error?: string;
    storage_path?: string;
    url?: string;
  };
  if (!res.ok || payload.error || !payload.url) {
    throw new Error(payload.error || `Upload image impossible (${res.status})`);
  }
  return { storage_path: payload.storage_path || storagePath, url: payload.url };
}

/** Upload générique staff sous resources/ ou network/ (Bunny kind=resource). */
export async function uploadStaffResourceFile(opts: {
  storagePath: string;
  file: File;
}): Promise<void> {
  return uploadContestResourceDocument(opts);
}

export async function deleteStaffResourceFile(opts: { storagePath: string }): Promise<void> {
  return deleteContestResourceDocument(opts);
}

export async function signStaffResourceUrl(opts: {
  storagePath: string;
  ttlSec?: number;
}): Promise<string> {
  return signContestResourceDocumentUrl(opts);
}
